import find from 'lodash/find'
import { buildTaxPack } from '@/helpers/buildTaxPack'
import { createPlaceholderIssuedStatement } from '@/helpers/placeholderIssuedStatement'
import type { DonorInformation } from '@/types'
import type { DonorDonation, DonorFinancialYear } from '@/types/donorFinancialYear'
import type { TaxPackData } from '@/types/taxPack'
import type { UserStatement } from '@/types/userStatement'

/**
 * Assembles statement PDF data for a period from API-shaped inputs (used by mock PDF route and tests).
 * When no issued statement exists for the period (e.g. £0 gap years), builds a placeholder
 * so paid donors can still download a PDF.
 */
export function resolveTaxPackForPeriod(
  period: string,
  financialYears: DonorFinancialYear[],
  statements: UserStatement[],
  manualDonations: DonorDonation[],
  fallbackDonor?: DonorInformation | null
): TaxPackData | null {
  let issuedStatement = find(statements, { period }) as UserStatement | undefined

  if (!issuedStatement) {
    const donor = fallbackDonor ?? statements.find((row) => row.donor)?.donor
    if (!donor) return null
    issuedStatement = createPlaceholderIssuedStatement(period, donor)
  }

  const financialYear =
    find(financialYears, { period }) ??
    ({
      period,
      donations: [],
      totalDonations: issuedStatement.totalDonated,
      totalGiftAid: issuedStatement.totalGiftAid,
    } satisfies DonorFinancialYear)

  return buildTaxPack(period, financialYear, issuedStatement, manualDonations)
}
